import axios from 'axios';
import {
  forEach,
  get,
  isArray,
  isObject,
  isString,
  keys,
} from 'lodash';

/**
 * Presigned S3 POST payload returned by api-service for bulk subscription CSV upload.
 *
 * @typedef {Object} SubscriptionImportPresignedUploadResponse
 * @property {string} url S3 POST endpoint.
 * @property {Object<string, string>|Array<{name: string, value: string}>} fields Form fields required by the signed policy. api-service includes `Content-Type` here when the policy requires it.
 * @property {'POST'|string} [method] HTTP method for upload (typically POST).
 * @property {number} [max_upload_bytes] Upper bound enforced by the upload policy.
 * @property {number} [max_subscription_import_data_rows] Max CSV data rows (excluding header), aligned with payments-service.
 */

/** Machine-readable error from api-service when CSV row count exceeds the configured cap. */
export const SUBSCRIPTION_IMPORT_ROW_LIMIT_ERROR_CODE = 'subscription_import_row_limit_exceeded';

/**
 * @param {Object|null|undefined} presign Presigned POST payload from api-service (see typedef above).
 * @returns {boolean}
 */
export function isPresignedSubscriptionImportUpload(presign) {
  const fields = presign && presign.fields;
  const hasFields = fields && isObject(fields);
  return Boolean(presign && presign.url && hasFields);
}

/**
 * @param {Object|null|undefined} presign
 * @returns {number|null}
 */
export function getMaxSubscriptionImportDataRows(presign) {
  const n = Number(get(presign, 'max_subscription_import_data_rows'));
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n;
}

/**
 * @param {FormData} formData
 * @param {Object<string, string>|Array<{name: string, value: string, key?: string}>|undefined} fields
 */
export function appendSubscriptionImportS3PostFields(formData, fields) {
  if (!fields) {
    return;
  }
  if (isArray(fields)) {
    forEach(fields, (entry) => {
      const { name: entryName, value, key } = entry;
      const name = entryName != null ? entryName : key;
      if (name == null) {
        return;
      }
      formData.append(name, value == null ? '' : String(value));
    });
  } else {
    forEach(keys(fields), (k) => {
      formData.append(k, fields[k]);
    });
  }
}

/**
 * Browser FormData POST to S3 using a presigned POST policy from api-service.
 *
 * @param {Object} presign
 * @param {File} file
 * @returns {Promise<void>}
 */
export async function postSubscriptionImportCsvToS3(presign, file) {
  const formData = new FormData();
  appendSubscriptionImportS3PostFields(formData, presign.fields);
  formData.append('file', file);

  try {
    await axios.post(presign.url, formData, {
      withCredentials: false,
      responseType: 'text',
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  } catch (err) {
    const status = get(err, 'response.status', 0);
    const raw = get(err, 'response.data');
    const text = isString(raw) ? raw : String(raw == null ? '' : raw);
    const uploadErr = new Error(`Could not upload the CSV to storage (${status}).`);
    uploadErr.uploadDetails = text.slice(0, 500);
    throw uploadErr;
  }
}
